import {
  ErrorSeverity,
  ErrorHandler,
} from '../types/error.types';

/**
 * Format API error response data (e.g. { statusCode, errorCode, errorMessage }) into a single display string.
 * Prefers structured format (errorMessage, errorCode, statusCode) then message/code, then fallback.
 */
export function formatStructuredApiErrorResponse(
  data: any,
  fallback?: string
): string {
  if (!data || typeof data !== 'object') {
    return fallback ?? 'Unknown error';
  }
  // Structured format: errorMessage, errorCode, statusCode
  const errorMessage = data.errorMessage ?? data.message;
  const errorCode = data.errorCode ?? data.code;
  const statusCode = data.statusCode;
  if (errorMessage != null && String(errorMessage).trim()) {
    let out = String(errorMessage).trim();
    if (errorCode != null && String(errorCode).trim()) {
      out += ` (${String(errorCode).trim()})`;
    }
    if (statusCode != null) {
      out = `${statusCode}: ${out}`;
    }
    return out;
  }
  // Fallback: existing shapes
  if (data.error?.message) return String(data.error.message);
  if (data.error && typeof data.error === 'string') return data.error;
  if (data.errors && Array.isArray(data.errors)) {
    return data.errors.join(', ');
  }
  return fallback ?? 'Unknown error';
}

/**
 * Helper function to format API errors for display
 * Extracts useful information from various API error formats
 */
export const formatApiError = (
  error: any
): {
  message: string;
  title?: string;
  severity: ErrorSeverity;
} => {
  // Default values
  let message = 'An unknown error occurred';
  let title = 'Error';
  let severity: ErrorSeverity = 'error';

  // No error provided
  if (!error) {
    return { message, title, severity };
  }

  // Axios error with response
  if (error.response) {
    const { status, data } = error.response;

    // Prefer structured format (errorMessage, errorCode, statusCode)
    if (data && typeof data === 'object') {
      const structured = formatStructuredApiErrorResponse(data);
      if (structured && structured !== 'Unknown error') {
        message = structured;
      }
    }

    // If no structured message, extract from other shapes
    if (message === 'An unknown error occurred') {
      if (typeof data === 'string') {
        message = data;
      } else if (data) {
        if (data.message) {
          message = data.message;
        } else if (data.error?.message) {
          message = data.error.message;
        } else if (data.error && typeof data.error === 'string') {
          message = data.error;
        }
      }
    }

    // Set title and severity based on status
    if (status === 400) {
      title = 'Bad Request';
    } else if (status === 401) {
      title = 'Authentication Required';
      message = 'Your session has expired. Please log in again.';
    } else if (status === 403) {
      title = 'Permission Denied';
      message = 'You do not have permission to perform this action.';
    } else if (status === 404) {
      title = 'Resource Not Found';
      severity = 'warning';
    } else if (status === 422) {
      title = 'Validation Error';
      // Format validation errors nicely
      if (data.errors && typeof data.errors === 'object') {
        const errorDetails = Object.entries(data.errors)
          .map(
            ([field, msgs]) =>
              `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`
          )
          .join('; ');

        message = errorDetails || message;
      }
    } else if (status >= 500) {
      title = 'Server Error';
      message = 'The server encountered an error. Please try again later.';
    }
  }
  // Network error (no response)
  else if (error.request) {
    title = 'Connection Error';
    message =
      'Unable to connect to the server. Please check your internet connection.';
    severity = 'warning';
  }
  // Simple error object or string
  else if (error.message) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  }

  return { message, title, severity };
};

/**
 * Helper function to handle errors in async operations
 * Use this with try/catch blocks for consistent error handling
 */
export const handleAsyncError = (
  error: any,
  errorHandler: ErrorHandler,
  context?: string
): void => {
  console.error(`Error${context ? ` in ${context}` : ''}:`, error);

  const formattedError = formatApiError(error);
  errorHandler({
    message: formattedError.message,
    title: formattedError.title,
    severity: formattedError.severity,
  });
};
